/**
 * EventView view component.
 * @module components/theme/View/EventView
 */

import React from "react";
import PropTypes from "prop-types";
import { hasBlocksData } from "@plone/volto/helpers/Blocks/Blocks";
import {
  flattenHTMLToAppURL,
  flattenToAppURL,
} from "@plone/volto/helpers/Url/Url";
import { Container as SemanticContainer } from "semantic-ui-react";
import RenderBlocks from "@plone/volto/components/theme/View/RenderBlocks";
import Icon from "@plone/volto/components/theme/Icon/Icon";
import calendarSVG from "@plone/volto/icons/calendar.svg";
import config from "@plone/volto/registry";
import { injectIntl, defineMessages } from "react-intl";

const messages = defineMessages({
  addToCalendar: {
    id: "ICS-Download",
    defaultMessage: "Add to calendar",
  },
});

// Pin an explicit locale AND an explicit timezone so the server and the
// client always compute the same date/time strings.
//
// - 'default' locale relies on the runtime's own locale setting.
// - Date methods like getHours()/getMinutes()/toLocaleString() without a
//   `timeZone` option silently use the runtime's own LOCAL TIMEZONE, not
//   a locale. If the Node SSR process and the browser have different
//   system timezones (common in dev/deploy setups), the same Date object
//   produces different hour/minute values on each side, which is a
//   structural mismatch severe enough to fail hydration. This matches
//   the site's Plone `plone.portal_timezone` setting.
const DATE_LOCALE = "en-ZA";
const EVENT_TIMEZONE = "Africa/Johannesburg";

// Reads the calendar year/month/day/hour/minute of a Date as they fall
// in EVENT_TIMEZONE, regardless of the runtime's own local timezone.
const getZonedDateParts = (date, timeZone = EVENT_TIMEZONE) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    year: Number(get("year")),
    month: Number(get("month")), // 1-based
    day: Number(get("day")),
    // Some engines return "24" for midnight with hour12: false.
    hour: Number(get("hour")) % 24,
    minute: Number(get("minute")),
  };
};

const isAllDayEvent = (content) => {
  if (content.whole_day === true) return true;
  if (content.start) {
    const startParts = getZonedDateParts(new Date(Date.parse(content.start)));
    if (startParts.hour === 0 && startParts.minute === 0) {
      if (content.end) {
        const endParts = getZonedDateParts(new Date(Date.parse(content.end)));
        if (
          (endParts.hour === 23 && endParts.minute === 59) ||
          (endParts.hour === 0 && endParts.minute === 0)
        )
          return true;
      } else {
        return true;
      }
    }
  }
  return false;
};

const EventTextfieldView = ({ content }) => {
  const Image = config.getComponent({ name: "Image" }).component;
  return (
    <React.Fragment>
      {content.title && (
        <h1 className="documentFirstHeading">{content.title}</h1>
      )}
      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}
      {content.preview_image && (
        <Image
          className="document-image ui right floated image"
          item={content}
          imageField="preview_image"
          alt=""
        />
      )}
      {content.text && (
        <div
          dangerouslySetInnerHTML={{
            __html: flattenHTMLToAppURL(content.text.data),
          }}
        />
      )}
    </React.Fragment>
  );
};

/**
 * EventView view component class.
 * @function EventView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const EventView = (props) => {
  const { content, intl } = props;
  const Container =
    config.getComponent({ name: "Container" }).component || SemanticContainer;

  const isOpenEnd = !!content.open_end;
  const allDay = isAllDayEvent(content);
  const start = content.start ? new Date(content.start) : null;
  const end = content.end ? new Date(content.end) : null;

  const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtDate = (d) => {
    const { day, month, year } = getZonedDateParts(d);
    return `${day} ${SHORT_MONTHS[month - 1]} ${year}`;
  };
  const fmtTime = (d) =>
    new Intl.DateTimeFormat(DATE_LOCALE, {
      timeZone: EVENT_TIMEZONE,
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(d);

  const startFmtDate = start ? fmtDate(start) : null;
  const startFmtTime = start ? fmtTime(start) : null;
  const endFmtDate = end ? fmtDate(end) : null;
  const endFmtTime = end ? fmtTime(end) : null;

  // The eventMetadata block (Volto core) duplicates the start/end display
  // already shown in the custom event-header-bar above, and its
  // FormattedDate usage doesn't pin a timeZone, causing a hydration
  // mismatch (server and client compute the hour in different local
  // timezones). Rather than patch or shadow that core block, drop it
  // from what gets passed to RenderBlocks -- the header bar already
  // covers the same information correctly.
  const contentForBlocks = React.useMemo(() => {
    if (!content.blocks || !content.blocks_layout) return content;
    const metadataBlockId = Object.keys(content.blocks).find(
      (id) => content.blocks[id]?.["@type"] === "eventMetadata",
    );
    if (!metadataBlockId) return content;
    const { [metadataBlockId]: _omit, ...remainingBlocks } = content.blocks;
    return {
      ...content,
      blocks: remainingBlocks,
      blocks_layout: {
        ...content.blocks_layout,
        items: content.blocks_layout.items.filter(
          (id) => id !== metadataBlockId,
        ),
      },
    };
  }, [content]);

  // Whether to show the "Add to calendar" button once the event has
  // passed. This depends on "now", which differs between the server
  // render and the client hydration pass, so it can't be computed
  // during the initial render without risking a hydration mismatch
  // (the button present on the server, absent on the client, or vice
  // versa, right around an event's end time). Default to showing it on
  // both the server render and the first client render, then correct
  // it after mount via useEffect, which never runs during SSR.
  const referenceDate = end || start;
  const [isPastEvent, setIsPastEvent] = React.useState(false);
  React.useEffect(() => {
    setIsPastEvent(referenceDate ? referenceDate < new Date() : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDate && referenceDate.getTime()]);

  return (
    <Container id="page-document" className="view-wrapper event-view">
      <div className="event-header-bar dates">
        <div className="event-header-bar__meta">
          {start && (
            <span className="event-header-bar__date">
              <time className="start" dateTime={content.start}>
                {allDay ? startFmtDate : `${startFmtDate} ${startFmtTime}`}
              </time>
              {!isOpenEnd && end && (
                <>
                  {' – '}
                  <time className="end" dateTime={content.end}>
                    {allDay ? endFmtDate : `${endFmtDate} ${endFmtTime}`}
                  </time>
                </>
              )}
            </span>
          )}
          {content?.head_title && (
            <span className="head-title">{content.head_title}</span>
          )}
          {content.location && (
            <span className="event-header-bar__location">
              {content.location}
            </span>
          )}
        </div>
        {!isPastEvent && (
          <a
            className="event-header-bar__ics"
            href={`${flattenToAppURL(content["@id"])}/ics_view`}
            target="_blank"
            rel="noreferrer"
            title={intl.formatMessage(messages.addToCalendar)}
            aria-label={intl.formatMessage(messages.addToCalendar)}
          >
            <Icon name={calendarSVG} aria-hidden="true" />
          </a>
        )}
      </div>
      {hasBlocksData(content) ? (
        <>
          <RenderBlocks {...props} content={contentForBlocks} />
        </>
      ) : (
        <EventTextfieldView {...props} />
      )}
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
EventView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    text: PropTypes.shape({
      data: PropTypes.string,
    }),
    attendees: PropTypes.arrayOf(PropTypes.string).isRequired,
    contact_email: PropTypes.string,
    contact_name: PropTypes.string,
    contact_phone: PropTypes.string,
    end: PropTypes.string.isRequired,
    event_url: PropTypes.string,
    location: PropTypes.string,
    open_end: PropTypes.bool,
    recurrence: PropTypes.any,
    start: PropTypes.string.isRequired,
    subjects: PropTypes.arrayOf(PropTypes.string).isRequired,
    whole_day: PropTypes.bool,
  }).isRequired,
};

export default injectIntl(EventView);